import { useState, useCallback, useEffect } from "react";
import { ArrowLeft, MapPin, Plus, Trash2, Star, Edit } from "lucide-react";
import { Button } from "@/app/components/button";
import { Input } from "@/app/components/input";
import { BottomNav } from "./bottom-nav";
import * as authClient from "@/app/api/auth-client";
import type { Profile } from "@/app/api/auth-client";
import {
  profileAddressesToList,
  addressListToProfileAddresses,
  type AddressListItem,
} from "@/app/utils/profile-mapper";
import { fetchCep } from "@/app/api/cep-client";

interface AddressesPageProps {
  onBack: () => void;
  activeTab: "home" | "favorites";
  onTabChange: (tab: "home" | "favorites") => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
  /** Called after addresses are saved so the app can refresh profile state (e.g. default address). */
  onProfileUpdated?: (profile: Profile) => void;
}

export function AddressesPage({
  onBack,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
  onProfileUpdated,
}: AddressesPageProps) {
  const [addresses, setAddresses] = useState<AddressListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const loadAddresses = useCallback(async () => {
    if (!authClient.getAccessToken()) {
      setAddresses([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setSaveError(null);
    try {
      const profile = await authClient.getProfile();
      setAddresses(profileAddressesToList(profile.addresses));
    } catch {
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  const persistAddresses = useCallback(
    async (newList: AddressListItem[], defaultId: string) => {
      setSaveError(null);
      const payload = addressListToProfileAddresses(newList, defaultId);
      const profile = await authClient.saveProfile({ addresses: payload });
      setAddresses(profileAddressesToList(profile.addresses));
      onProfileUpdated?.(profile);
    },
    [onProfileUpdated]
  );

  const handleSetMain = useCallback(
    async (id: string) => {
      const newList = addresses.map((addr) => ({
        ...addr,
        isMain: addr.id === id,
      }));
      setAddresses(newList);
      try {
        await persistAddresses(newList, id);
      } catch (e) {
        setSaveError(e instanceof Error ? e.message : "Não foi possível salvar.");
        setAddresses(addresses);
      }
    },
    [addresses, persistAddresses]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      const removed = addresses.find((a) => a.id === id);
      const filtered = addresses.filter((addr) => addr.id !== id);
      if (filtered.length === 0) {
        setAddresses([]);
        try {
          await authClient.saveProfile({ addresses: [] });
          onProfileUpdated?.(await authClient.getProfile());
        } catch (e) {
          setSaveError(e instanceof Error ? e.message : "Não foi possível excluir.");
        }
        return;
      }
      const rekeyed = filtered.map((a, i) => ({ ...a, id: String(i) }));
      if (removed?.isMain) rekeyed[0].isMain = true;
      const defaultId = rekeyed.find((a) => a.isMain)!.id;
      setAddresses(rekeyed);
      try {
        await persistAddresses(rekeyed, defaultId);
      } catch (e) {
        setSaveError(e instanceof Error ? e.message : "Não foi possível excluir.");
        setAddresses(addresses);
      }
    },
    [addresses, persistAddresses, onProfileUpdated]
  );

  const handleAddAddress = useCallback(
    async (address: Omit<AddressListItem, "id" | "isMain">) => {
      const newItem: AddressListItem = {
        ...address,
        id: String(addresses.length),
        isMain: addresses.length === 0,
      };
      const newList = [...addresses, newItem];
      setAddresses(newList);
      setIsAddingNew(false);
      try {
        await persistAddresses(newList, newItem.isMain ? newItem.id : addresses.find((a) => a.isMain)!.id);
      } catch (e) {
        setSaveError(e instanceof Error ? e.message : "Não foi possível salvar.");
        setAddresses(addresses);
      }
    },
    [addresses, persistAddresses]
  );

  const handleUpdateAddress = useCallback(
    async (id: string, address: Omit<AddressListItem, "id" | "isMain">) => {
      const newList = addresses.map((addr) =>
        addr.id === id ? { ...addr, ...address } : addr
      );
      setAddresses(newList);
      setEditingId(null);
      const defaultId = addresses.find((a) => a.isMain)!.id;
      try {
        await persistAddresses(newList, defaultId);
      } catch (e) {
        setSaveError(e instanceof Error ? e.message : "Não foi possível salvar.");
        setAddresses(addresses);
      }
    },
    [addresses, persistAddresses]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-24 flex items-center justify-center">
        <p className="text-muted-foreground">Carregando endereços...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl text-white">Meus Endereços</h1>
              <p className="text-sm text-white/80">
                {addresses.length} {addresses.length === 1 ? "endereço salvo" : "endereços salvos"}
              </p>
            </div>
            <button
              onClick={() => setIsAddingNew(true)}
              className="p-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-lg transition-colors"
            >
              <Plus className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {saveError && (
          <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
            {saveError}
          </div>
        )}

        {/* Add New Address Form */}
        {isAddingNew && (
          <div className="mb-6">
            <AddressForm
              onSubmit={handleAddAddress}
              onCancel={() => setIsAddingNew(false)}
              title="Adicionar Novo Endereço"
            />
          </div>
        )}

        {/* Address List */}
        {addresses.length === 0 && !isAddingNew ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-10 h-10 text-secondary-foreground" />
            </div>
            <h3 className="mb-2">Nenhum endereço cadastrado</h3>
            <p className="text-muted-foreground mb-6">
              Adicione seu primeiro endereço para começar
            </p>
            <Button onClick={() => setIsAddingNew(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Endereço
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {[...addresses].sort((a, b) => (b.isMain ? 1 : 0) - (a.isMain ? 1 : 0)).map((address) => (
              <div key={address.id}>
                {editingId === address.id ? (
                  <AddressForm
                    initialData={address}
                    onSubmit={(data) => handleUpdateAddress(address.id, data)}
                    onCancel={() => setEditingId(null)}
                    title="Editar Endereço"
                  />
                ) : (
                  <AddressCard
                    address={address}
                    canDelete={addresses.length > 1}
                    onSetMain={handleSetMain}
                    onDelete={handleDelete}
                    onEdit={() => setEditingId(address.id)}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNav
        activeTab={activeTab}
        onTabChange={(tab) => onTabChange(tab as "home" | "favorites")}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
}

interface AddressCardProps {
  address: AddressListItem;
  /** When false (e.g. only one address), delete is disabled; customer must add another and set it main first. */
  canDelete: boolean;
  onSetMain: (id: string) => void;
  onDelete: (id: string) => void;
  onEdit: () => void;
}

function AddressCard({ address, canDelete, onSetMain, onDelete, onEdit }: AddressCardProps) {
  const formatCepDisplay = (cep: string) => {
    const d = cep.replace(/\D/g, "");
    if (d.length <= 5) return d;
    return `${d.slice(0, 5)}-${d.slice(5)}`;
  };
  return (
    <div className={`bg-card border rounded-lg p-4 ${address.isMain ? "border-primary" : "border-border"}`}>
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
          address.isMain ? "bg-primary" : "bg-secondary"
        }`}>
          <MapPin className={`w-6 h-6 ${address.isMain ? "text-primary-foreground" : "text-secondary-foreground"}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            {address.isMain && (
              <div className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded-md text-xs font-medium">
                <Star className="w-3 h-3 fill-current" />
                Principal
              </div>
            )}
          </div>

          <div className="space-y-1">
            <p className="font-medium">{address.rua}, {address.numero}</p>
            {address.complemento && (
              <p className="text-sm text-muted-foreground">{address.complemento}</p>
            )}
            <p className="text-sm text-muted-foreground">CEP: {formatCepDisplay(address.cep)}</p>
          </div>

          <div className="flex items-center gap-2 mt-4">
            {!address.isMain && (
              <button
                onClick={() => onSetMain(address.id)}
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                <Star className="w-3 h-3" />
                Definir como principal
              </button>
            )}
            <button
              onClick={onEdit}
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              <Edit className="w-3 h-3" />
              Editar
            </button>
            {canDelete ? (
              <button
                onClick={() => onDelete(address.id)}
                className="text-sm text-destructive hover:underline flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" />
                Excluir
              </button>
            ) : (
              <span
                className="text-sm text-muted-foreground cursor-not-allowed flex items-center gap-1"
                title="É necessário ter pelo menos um endereço. Adicione outro e defina-o como principal para poder excluir este."
              >
                <Trash2 className="w-3 h-3" />
                Excluir
              </span>
            )}
          </div>
          {!canDelete && (
            <p className="text-xs text-muted-foreground mt-2">
              É necessário ter pelo menos um endereço. Adicione outro e defina-o como principal para poder excluir este.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

interface AddressFormProps {
  initialData?: AddressListItem;
  onSubmit: (address: Omit<AddressListItem, "id" | "isMain">) => void;
  onCancel: () => void;
  title: string;
}

function AddressForm({ initialData, onSubmit, onCancel, title }: AddressFormProps) {
  const [formData, setFormData] = useState({
    cep: initialData?.cep?.replace(/\D/g, "") ?? "",
    rua: initialData?.rua ?? "",
    numero: initialData?.numero ?? "",
    complemento: initialData?.complemento ?? "",
    neighborhood: initialData?.neighborhood ?? "",
    city: initialData?.city ?? "",
    state: initialData?.state ?? "SP",
  });
  const [cepLookupLoading, setCepLookupLoading] = useState(false);
  const [cepLookupError, setCepLookupError] = useState<string | null>(null);

  const handleCEPChange = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 8);
    setFormData((prev) => ({ ...prev, cep: digits }));
    setCepLookupError(null);
  };

  const handleCEPLookup = async () => {
    if (formData.cep.length !== 8) return;
    setCepLookupError(null);
    setCepLookupLoading(true);
    try {
      const result = await fetchCep(formData.cep);
      setFormData((prev) => ({
        ...prev,
        rua: result.street,
        neighborhood: result.neighborhood,
        city: result.city,
        state: result.state,
      }));
    } catch (e) {
      setCepLookupError(e instanceof Error ? e.message : "CEP não encontrado.");
    } finally {
      setCepLookupLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.cep.length === 8 && formData.rua && formData.numero && formData.neighborhood && formData.city && formData.state) {
      onSubmit({
        cep: formData.cep,
        rua: formData.rua,
        numero: formData.numero,
        complemento: formData.complemento,
        neighborhood: formData.neighborhood,
        city: formData.city,
        state: formData.state,
      });
    }
  };

  const formatCepDisplay = (cep: string) => {
    if (cep.length <= 5) return cep;
    return `${cep.slice(0, 5)}-${cep.slice(5)}`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h3 className="mb-4">{title}</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            CEP <span className="text-destructive">*</span>
          </label>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="00000-000"
              value={formatCepDisplay(formData.cep)}
              onChange={(e) => handleCEPChange(e.target.value)}
              maxLength={9}
              required
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleCEPLookup}
              disabled={formData.cep.length !== 8 || cepLookupLoading}
            >
              {cepLookupLoading ? "Buscando..." : "Buscar"}
            </Button>
          </div>
          {cepLookupError && <p className="text-sm text-destructive mt-1">{cepLookupError}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Rua / Avenida <span className="text-destructive">*</span>
          </label>
          <Input
            type="text"
            placeholder="Nome da rua ou avenida"
            value={formData.rua}
            onChange={(e) => setFormData({ ...formData, rua: e.target.value })}
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Número <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              placeholder="Nº"
              value={formData.numero}
              onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
              required
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium mb-2">
              Bairro <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              placeholder="Bairro"
              value={formData.neighborhood}
              onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Cidade <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              placeholder="Cidade"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              Estado <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              placeholder="UF"
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase().slice(0, 2) })}
              maxLength={2}
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Complemento
          </label>
          <Input
            type="text"
            placeholder="Apartamento, sala, etc. (opcional)"
            value={formData.complemento}
            onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
          />
        </div>

        <div className="flex gap-3 pt-2">
          <Button type="submit" className="flex-1">
            {initialData ? "Salvar Alterações" : "Adicionar Endereço"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  );
}
